import { CollisionSensitivity, FleetDefaults, MountedOnType } from "@app/shared/src/models";
import { MPS_TO_MPH } from "@app/shared/src/components_v2";
import {
    MAX_ZONE_LENGTH_M,
    MAX_ZONE_WIDTH_M,
    MIN_ZONE_DIM_M,
    zoneLengthOutOfRange,
    zoneWidthOutOfRange,
} from "@app/shared/src/helpers/sidePassGeometry";
import { AlertSettings, alertSettingsFromDefaults } from "./alertSettings";

/**
 * What a director is asked for when adding a truck, and whether they have answered enough to move on.
 *
 * Kept apart from the wizard component so the step gates are testable without rendering a modal, and
 * so the commit sequence can take a plain object rather than reaching into React state.
 *
 * Every number the user edits is held as the *string they typed*, in the unit shown on screen —
 * mph, feet, seconds. A half-typed "1" on the way to "15" must not be parsed and pushed to a truck,
 * and a field cleared to empty must stay empty rather than snapping to 0. Parsing happens once, at
 * the end, in `draftToPlan`.
 */

/** Metres per foot — mounting is asked in feet because that is what an installer's tape reads. */
export const FEET_TO_METRES = 0.3048;

export type WizardStep = "key" | "group" | "identity" | "mounting" | "area" | "alerts" | "review";

/** In order. The shell walks this array rather than hard-coding what follows what. */
export const WIZARD_STEPS: WizardStep[] = ["key", "group", "identity", "mounting", "area", "alerts", "review"];

export const STEP_TITLES: Record<WizardStep, { title: string; subtitle: string }> = {
    key: { title: "Add a device", subtitle: "Find the 7-character key on the label on the underside of the unit, next to the Ethernet jack, or scan the QR on the box" },
    group: { title: "Which group?", subtitle: "Groups hold the trucks in one yard, region or contract" },
    identity: { title: "Name the truck", subtitle: "The number on the door, or whatever the crew calls this unit" },
    mounting: { title: "How is it mounted?", subtitle: "Where the unit sits on the equipment, and what that equipment is" },
    area: { title: "Protected area", subtitle: "The footprint the truck defends — this sizes the impending-collision zone" },
    alerts: { title: "Alerts", subtitle: "What this truck warns about, starting from your fleet defaults" },
    review: { title: "Review", subtitle: "Nothing has been created yet — this is the last stop before it is" },
};

/** A group the truck can join: one that exists, or one being named here. */
export type GroupChoice = { kind: "existing"; id: string; name: string } | { kind: "new"; name: string };

export type AddDeviceDraft = {
    deviceKey: string;
    /** Filled by the key step once the server confirms the key is real and unclaimed. */
    verifiedDeviceId?: string;

    group?: GroupChoice;

    /** Names both the location and the device — one location is one TMA is one device. */
    truckName: string;

    heightFeet: string;
    /** Signed: negative is left of centre, positive right, matching the cab's OffsetFromCentre. */
    offsetFeet: string;
    mountedOn?: MountedOnType;

    widthFeet: string;
    lengthFeet: string;

    /** Alert thresholds in display units, seeded from the fleet defaults. */
    approachMph: string;
    reactionSeconds: string;
    sensitivity: CollisionSensitivity;
    speedingEnabled: boolean;
    speedingMph: string;
    leftPassEnabled: boolean;
    rightPassEnabled: boolean;
    sidePassMph: string;
    leftPassLanes: number;
    rightPassLanes: number;
};

const mph = (mps: number) => String(Math.round(mps * MPS_TO_MPH));
const feet = (metres: number) => (metres / FEET_TO_METRES).toFixed(1);

/**
 * The starting draft: the fleet's stated defaults, not blanks.
 *
 * A director adding their twentieth truck should be able to press through without retyping settings
 * they already stated once — and a value that arrived from the fleet record is a value someone
 * chose, which is exactly what makes pre-filling it defensible rather than a guess.
 */
export function initialDraft(defaults: FleetDefaults): AddDeviceDraft {
    const alerts = alertSettingsFromDefaults(defaults);
    return {
        deviceKey: "",
        truckName: "",

        heightFeet: "",
        offsetFeet: "0",
        mountedOn: undefined,

        widthFeet: feet(defaults.tmaWidthMetres),
        lengthFeet: feet(defaults.tmaLengthMetres),

        approachMph: mph(alerts.minApproachSpeedMps),
        reactionSeconds: alerts.reactionTimeSeconds.toFixed(1),
        sensitivity: alerts.sensitivity,
        speedingEnabled: alerts.speedingEnabled,
        speedingMph: mph(alerts.speedingThresholdMps),
        leftPassEnabled: alerts.leftPassEnabled,
        rightPassEnabled: alerts.rightPassEnabled,
        sidePassMph: mph(alerts.sidePassMinSpeedMps),
        leftPassLanes: alerts.leftPassLanes,
        rightPassLanes: alerts.rightPassLanes,
    };
}

const positive = (v: string) => Number.isFinite(Number(v)) && Number(v) > 0;
/** Offset alone may be zero or negative — dead centre and left-of-centre are both real answers. */
const finite = (v: string) => v.trim() !== "" && Number.isFinite(Number(v));

/**
 * Why this step cannot be left yet, or undefined when it can.
 *
 * Returning the reason rather than a boolean means the Next button can say what is missing instead
 * of sitting greyed out with no explanation — the failure mode where a director cannot tell whether
 * the form is broken or they missed a field.
 */
export function stepBlocker(step: WizardStep, draft: AddDeviceDraft): string | undefined {
    switch (step) {
        case "key":
            return draft.verifiedDeviceId ? undefined : "Check the device key first";
        case "group":
            if (!draft.group) return "Pick a group, or add a new one";
            if (draft.group.kind === "new" && !draft.group.name.trim()) return "Name the new group";
            return undefined;
        case "identity":
            return draft.truckName.trim() ? undefined : "Enter a truck number or equipment name";
        case "mounting":
            if (!positive(draft.heightFeet)) return "Enter the mounting height";
            if (!finite(draft.offsetFeet)) return "Enter the offset from centre — 0 if it is centred";
            if (!draft.mountedOn) return "Pick what the unit is mounted on";
            return undefined;
        case "area":
            if (!positive(draft.widthFeet)) return "Enter the protected width";
            if (!positive(draft.lengthFeet)) return "Enter the protected length";
            // The layout editor that draws this zone will not render it outside these bounds.
            if (zoneWidthOutOfRange(Number(draft.widthFeet) * FEET_TO_METRES))
                return `Width must be between ${feet(MIN_ZONE_DIM_M)} and ${feet(MAX_ZONE_WIDTH_M)} ft`;
            if (zoneLengthOutOfRange(Number(draft.lengthFeet) * FEET_TO_METRES))
                return `Length must be between ${feet(MIN_ZONE_DIM_M)} and ${feet(MAX_ZONE_LENGTH_M)} ft`;
            return undefined;
        case "alerts":
            if (!positive(draft.approachMph)) return "Enter a minimum approach speed";
            if (!positive(draft.reactionSeconds)) return "Enter a reaction time";
            if (draft.speedingEnabled && !positive(draft.speedingMph)) return "Enter a speeding threshold";
            if ((draft.leftPassEnabled || draft.rightPassEnabled) && !positive(draft.sidePassMph))
                return "Enter a side-pass minimum speed";
            return undefined;
        case "review":
            return undefined;
    }
}

/** Everything the commit needs, in server units, parsed exactly once. */
export type ProvisionPlan = {
    deviceId: string;
    deviceKey: string;
    truckName: string;
    group: GroupChoice;

    heightMetres: number;
    offsetMetres: number;
    mountedOn: MountedOnType;

    widthMetres: number;
    lengthMetres: number;

    alerts: AlertSettings;
};

/**
 * Convert a fully answered draft into the plan the commit runs.
 *
 * Returns undefined when any step is still blocked, so there is exactly one gate between "the form
 * looks done" and "we start writing to the server" — the wizard cannot half-commit a truck because
 * a step was skipped by a stale button state.
 */
export function draftToPlan(draft: AddDeviceDraft, defaults: FleetDefaults): ProvisionPlan | undefined {
    if (WIZARD_STEPS.some((s) => stepBlocker(s, draft))) return undefined;
    if (!draft.verifiedDeviceId || !draft.group || !draft.mountedOn) return undefined;

    const base = alertSettingsFromDefaults(defaults);

    return {
        deviceId: draft.verifiedDeviceId,
        deviceKey: draft.deviceKey.trim(),
        truckName: draft.truckName.trim(),
        group: draft.group,

        heightMetres: Number(draft.heightFeet) * FEET_TO_METRES,
        offsetMetres: Number(draft.offsetFeet) * FEET_TO_METRES,
        mountedOn: draft.mountedOn,

        widthMetres: Number(draft.widthFeet) * FEET_TO_METRES,
        lengthMetres: Number(draft.lengthFeet) * FEET_TO_METRES,

        alerts: {
            ...base,
            minApproachSpeedMps: Number(draft.approachMph) / MPS_TO_MPH,
            reactionTimeSeconds: Number(draft.reactionSeconds),
            sensitivity: draft.sensitivity,
            speedingEnabled: draft.speedingEnabled,
            speedingThresholdMps: Number(draft.speedingMph) / MPS_TO_MPH,
            leftPassEnabled: draft.leftPassEnabled,
            rightPassEnabled: draft.rightPassEnabled,
            sidePassMinSpeedMps: Number(draft.sidePassMph) / MPS_TO_MPH,
            leftPassLanes: draft.leftPassLanes,
            rightPassLanes: draft.rightPassLanes,
        },
    };
}
